import React, { useState } from 'react';
import { Mail, X, Send, Loader2 } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { masterSupabase } from '../../../lib/supabase-master';

/**
 * EmailShareButton Component
 * Generates PDF and sends bank report via email through Roundcube
 * 
 * Features:
 * - PDF generation from slides
 * - Email modal with recipient input
 * - Roundcube API integration
 * - Report storage in Supabase
 * - Progress tracking
 */
const EmailShareButton = ({ reportData, propertyId, propertyName, userId, deckRef }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailSubject, setEmailSubject] = useState(`Bankreport: ${propertyName}`);
  const [emailMessage, setEmailMessage] = useState(
    `Sehr geehrte Damen und Herren,\n\nanbei erhalten Sie den Bankreport für die Immobilie "${propertyName}".\n\nMit freundlichen Grüßen`
  );
  const [pdfBlob, setPdfBlob] = useState(null);
  const [error, setError] = useState(null);

  const generatePDF = async () => {
    if (!deckRef.current) {
      throw new Error('Keine Slides gefunden');
    }

    setIsGenerating(true);
    setProgress(0);
    setError(null);

    try {
      const deck = deckRef.current;
      
      // Wait for slides to be fully rendered
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const slides = document.querySelectorAll('.reveal .slides > section');
      
      console.log('[EMAIL] Found', slides.length, 'slides');
      
      if (slides.length === 0) {
        // Try alternative selector
        const altSlides = document.querySelectorAll('.reveal section');
        console.log('[EMAIL] Alternative selector found', altSlides.length, 'slides');
        
        if (altSlides.length === 0) {
          throw new Error('Keine Slides gefunden. Bitte warten Sie, bis die Präsentation vollständig geladen ist.');
        }
      }

      // Create PDF (landscape A4)
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [1920, 1080]
      });

      // Capture each slide
      for (let i = 0; i < slides.length; i++) {
        setProgress(Math.round(((i + 1) / slides.length) * 50)); // 0-50% for PDF generation

        // Navigate to slide
        deck.slide(i);
        await new Promise(resolve => setTimeout(resolve, 500));

        // Capture slide
        const canvas = await html2canvas(slides[i], {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/png');

        if (i > 0) {
          pdf.addPage();
        }

        pdf.addImage(imgData, 'PNG', 0, 0, 1920, 1080);
      }

      // Convert to Blob
      const blob = pdf.output('blob');
      setPdfBlob(blob);
      setProgress(50);

      console.log('[EMAIL] PDF generated:', blob.size, 'bytes');
      return blob;
    } catch (error) {
      console.error('[EMAIL] PDF generation error:', error);
      setError('Fehler beim Erstellen des PDFs: ' + error.message);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  const saveReportToDatabase = async (pdfBlob) => {
    try {
      setProgress(60);

      const { data, error } = await masterSupabase
        .from('bank_reports')
        .insert({
          property_id: propertyId,
          user_id: userId,
          property_name: propertyName,
          slides_count: reportData?.slides?.length || 0,
          report_data: reportData,
          slides: reportData?.slides || [],
          shared_via_email: true,
          email_sent_at: new Date().toISOString(),
          recipient_email: recipientEmail
        })
        .select()
        .single();

      if (error) throw error;

      console.log('[EMAIL] Report saved to database:', data.id);
      setProgress(70);
      return data;
    } catch (error) {
      console.error('[EMAIL] Database save error:', error);
      // Non-critical error - continue with email sending
      setProgress(70);
      return null;
    }
  };

  const sendEmail = async (pdfBlob) => {
    try {
      setProgress(80);

      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
      });
      reader.readAsDataURL(pdfBlob);
      const pdfBase64 = await base64Promise;

      // Generate filename
      const date = new Date().toISOString().split('T')[0];
      const filename = `Bankreport_${propertyName.replace(/\s+/g, '_')}_${date}.pdf`;

      // Send via Roundcube API (or backend endpoint)
      const response = await fetch('/api/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: recipientEmail,
          subject: emailSubject,
          message: emailMessage,
          attachment: {
            filename,
            content: pdfBase64,
            contentType: 'application/pdf'
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'E-Mail konnte nicht gesendet werden');
      }

      setProgress(100);
      console.log('[EMAIL] Email sent successfully to:', recipientEmail);
      return true;
    } catch (error) {
      console.error('[EMAIL] Send error:', error);
      throw error;
    }
  };

  const handleSendEmail = async () => {
    if (!recipientEmail || !recipientEmail.includes('@')) {
      setError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }

    setIsSending(true);
    setError(null);
    setProgress(0);

    try {
      // Step 1: Generate PDF (if not already generated)
      let blob = pdfBlob;
      if (!blob) {
        blob = await generatePDF();
      }

      // Step 2: Save report to database
      await saveReportToDatabase(blob);

      // Step 3: Send email
      await sendEmail(blob);

      // Success
      alert(`E-Mail erfolgreich an ${recipientEmail} gesendet!`);
      setIsModalOpen(false);
      setRecipientEmail('');
      setPdfBlob(null);
      setProgress(0);
    } catch (error) {
      setError(error.message || 'Fehler beim Versenden der E-Mail');
    } finally {
      setIsSending(false);
    }
  };

  const handleOpenModal = async () => {
    setIsModalOpen(true);
    setError(null);
    
    // Pre-generate PDF in background
    if (!pdfBlob) {
      try {
        await generatePDF();
      } catch (error) {
        // Error already handled in generatePDF
      }
    }
  };

  return (
    <>
      {/* Email Button */}
      <button
        onClick={handleOpenModal}
        className="relative px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-lg backdrop-blur-sm transition-all duration-200 hover:scale-105"
        title="Per E-Mail versenden"
      >
        <div className="flex items-center space-x-2">
          <Mail className="w-5 h-5" />
          <span className="text-sm font-medium">E-Mail</span>
        </div>
      </button>

      {/* Email Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-lg shadow-2xl max-w-lg w-full border border-white/10">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Mail className="w-6 h-6 text-blue-400" />
                Bankreport per E-Mail versenden
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors"
                disabled={isSending}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Progress */}
              {(isGenerating || isSending) && (
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm text-gray-400 mb-2">
                    <span>{isGenerating ? 'PDF wird erstellt...' : 'E-Mail wird versendet...'}</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Recipient Email */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Empfänger E-Mail *
                </label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="banker@bank.de"
                  className="w-full px-4 py-2 bg-gray-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSending}
                  required
                />
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Betreff
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSending}
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nachricht
                </label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={5}
                  className="w-full px-4 py-2 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  disabled={isSending}
                />
              </div>

              {/* PDF Status */}
              {pdfBlob && !isGenerating && (
                <div className="flex items-center gap-2 text-sm text-green-400">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  PDF bereit ({(pdfBlob.size / 1024 / 1024).toFixed(2)} MB)
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                disabled={isSending}
              >
                Abbrechen
              </button>
              <button
                onClick={handleSendEmail}
                disabled={isSending || isGenerating || !recipientEmail}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Senden
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmailShareButton;

